import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import '../styles/LogoUpload.css';
import { API_BASE_URL } from '../utils/apiConfig';

const LogoUpload = ({ onLogoChange }) => {
  const [currentLogo, setCurrentLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [error, setError] = useState('');
  const uploadLockRef = useRef(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch current logo on component mount
  const fetchCurrentLogo = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        setError('Please log in to view organisation logo.');
        setCurrentLogo(null);
        return;
      }
      const requestId = `logo-current-admin-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      const response = await axios.get(`${API_BASE_URL}/logo/current`, {
        headers: {
          ...headers,
          'X-Request-Id': requestId,
          'X-Request-Source': 'admin-logo-upload'
        }
      });
      if (response.data.success && response.data.data) {
        setCurrentLogo(response.data.data);
        if (onLogoChange) {
          onLogoChange(response.data.data);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setCurrentLogo(null);
      } else {
        setError(error.response?.data?.error || 'Failed to fetch logo');
      }
      console.error('Error fetching current logo:', error);
    }
  }, [onLogoChange]);

  useEffect(() => {
    fetchCurrentLogo();
  }, [fetchCurrentLogo]);

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, JPG, PNG, and SVG files are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      setError('File size too large. Maximum size is 5MB.');
      return false;
    }

    setError('');
    return true;
  };

  const handleFileUpload = async (file) => {
    if (uploadLockRef.current) {
      console.warn('[LogoUpload] Upload blocked: another upload is in progress.', {
        fileName: file?.name,
        size: file?.size,
        type: file?.type
      });
      return;
    }

    uploadLockRef.current = true;
    if (!validateFile(file)) {
      console.warn('[LogoUpload] Upload rejected by validation.', {
        fileName: file?.name,
        size: file?.size,
        type: file?.type
      });
      uploadLockRef.current = false;
      return;
    }

    setUploading(true);
    setUploadMessage('');
    setError('');
    console.info('[LogoUpload] Starting upload.', {
      fileName: file?.name,
      size: file?.size,
      type: file?.type
    });

    const formData = new FormData();
    formData.append('logo', file);
    const requestId = `logo-upload-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
        'X-Request-Id': requestId
      };
      if (!headers.Authorization) {
        setError('Please log in to upload a logo.');
        setUploading(false);
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/logo/upload`, formData, {
        headers,
      });

      if (response.data.success) {
        console.info('[LogoUpload] Upload succeeded.', {
          logoPath: response.data?.data?.logoPath,
          logoFileName: response.data?.data?.logoFileName
        });
        setUploadMessage('Logo uploaded successfully!');
        await fetchCurrentLogo(); // Refresh current logo
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setUploadMessage('');
        }, 3000);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(error.response?.data?.error || 'Failed to upload logo');
      }
      console.warn('[LogoUpload] Upload failed.', {
        requestId,
        status: error.response?.status,
        message: error.response?.data?.error || error.message
      });
      console.error('Error uploading logo:', error);
    } finally {
      setUploading(false);
      console.info('[LogoUpload] Upload finished.');
      uploadLockRef.current = false;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the current logo?')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        setError('Please log in to delete a logo.');
        return;
      }

      const response = await axios.delete(`${API_BASE_URL}/logo/delete`, { headers });
      if (response.data.success) {
        setCurrentLogo(null);
        setUploadMessage('Logo deleted successfully!');
        if (onLogoChange) {
          onLogoChange(null);
        }
        
        setTimeout(() => {
          setUploadMessage('');
        }, 3000);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(error.response?.data?.error || 'Failed to delete logo');
      }
      console.error('Error deleting logo:', error);
    }
  };

  return (
    <div className="logo-upload-container">
      <h3>Company Logo</h3>
      <p className="logo-note">
        Recommended: SVG (preferred) or PNG, landscape ratio ~4:1. For best web + PDF clarity,
        use 800x200 px (or 1200x300 px) and keep key details within a 240x60 px safe area.
        Max file size: 5MB.
      </p>
      
      {currentLogo ? (
        <div className="current-logo-section">
          <div className="logo-preview">
            <img 
              src={currentLogo.fullUrl || currentLogo.logoPath} 
              alt="Current Logo" 
              className="logo-image"
            />
          </div>
          <div className="logo-info">
            <p><strong>File:</strong> {currentLogo.logoFileName}</p>
            <p><strong>Uploaded:</strong> {new Date(currentLogo.logoUploadDate).toLocaleDateString()}</p>
          </div>
          <div className="logo-actions">
            <button 
              onClick={handleDeleteLogo}
              className="btn btn-delete"
            >
              Delete Logo
            </button>
          </div>
        </div>
      ) : (
        <div className="no-logo-section">
          <p>No logo uploaded yet.</p>
        </div>
      )}

      <div 
        className={`upload-area ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="upload-content">
          {uploading ? (
            <div className="uploading-state">
              <div className="upload-spinner"></div>
              <p>Uploading logo...</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <p className="upload-text">
                {currentLogo ? 'Drop new logo here or click to replace' : 'Drop logo here or click to upload'}
              </p>
              <p className="upload-subtext">
                Supports JPEG, JPG, PNG, SVG (max 5MB)
              </p>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                onChange={handleFileChange}
                className="file-input"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="btn btn-upload">
                Choose File
              </label>
            </>
          )}
        </div>
      </div>

      {uploadMessage && (
        <div className="success-message">
          {uploadMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default LogoUpload;